import { useCallback, useMemo, useSyncExternalStore } from "react";
import { settingsStore } from "../settings/store";
import type { SettingKey, SettingsValues } from "../settings/schema";
import type { RegistryEntry } from "../settings/types";

export function useSetting<K extends SettingKey>(key: K): [SettingsValues[K], (v: SettingsValues[K]) => Promise<void>] {
  const subscribe = useCallback((fn: () => void) => settingsStore.subscribe(key, fn), [key]);
  const value = useSyncExternalStore(subscribe, () => settingsStore.get(key));
  const setValue = useCallback((v: SettingsValues[K]) => settingsStore.set(key, v), [key]);
  return [value, setValue];
}

export function useSettingValue<K extends SettingKey>(key: K): SettingsValues[K] {
  return useSetting(key)[0];
}

export function useIsAvailable(key: string): boolean {
  return useSyncExternalStore(settingsStore.subscribeMeta, () => settingsStore.isAvailable(key));
}

export function useSettingsStatus() {
  return useSyncExternalStore(settingsStore.subscribeMeta, settingsStore.getStatus);
}

export function useSettingGroups(): { group: string; entries: RegistryEntry[] }[] {
  const entries = useSyncExternalStore(settingsStore.subscribeMeta, settingsStore.getEntries);

  return useMemo(() => {
    const acc: Record<string, RegistryEntry[]> = {};
    const order: string[] = [];

    entries.forEach((e) => {
      const g = e.def.group ?? "etc";
      if (!acc[g]) {
        acc[g] = [];
        order.push(g); // 등장 순서 보존
      }
      acc[g].push(e);
    });

    return order.map((group) => ({ group, entries: acc[group] }));
  }, [entries]);
}
